/**
 * ContractorJobPage - Unified Contractor Job View
 * ================================================
 * Uses JobCard for consistent job display.
 * Maintains all existing form functionality (accept, schedule, quote, uploads).
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import JobCard from "../components/JobCard";

const API = process.env.REACT_APP_BACKEND_URL || "";

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function safeNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

// ============================================================================
// UI COMPONENTS
// ============================================================================

function Card({ children, className = "" }) {
  return (
    <div className={`bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden ${className}`}>
      {children}
    </div>
  );
}

function Input({ label, ...props }) {
  return (
    <div className="space-y-1.5">
      {label && <label className="block text-sm font-medium text-slate-600">{label}</label>}
      <input className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-800 placeholder-slate-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all" {...props} />
    </div>
  );
}

function Textarea({ label, ...props }) {
  return (
    <div className="space-y-1.5">
      {label && <label className="block text-sm font-medium text-slate-600">{label}</label>}
      <textarea className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-800 placeholder-slate-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all resize-none" rows={4} {...props} />
    </div>
  );
}

function Button({ children, variant = "primary", loading, disabled, className = "", ...props }) {
  const variants = {
    primary: "bg-teal-600 hover:bg-teal-700 text-white",
    success: "bg-emerald-600 hover:bg-emerald-700 text-white",
    danger: "bg-red-500 hover:bg-red-600 text-white",
    secondary: "bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200",
    outline: "border-2 border-teal-600 text-teal-600 hover:bg-teal-50",
    dark: "bg-slate-900 hover:bg-slate-800 text-white",
  };
  return (
    <button className={`inline-flex items-center justify-center gap-2 px-5 py-3 font-semibold rounded-2xl transition-all disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant]} ${className}`} disabled={disabled || loading} {...props}>
      {loading && <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>}
      {children}
    </button>
  );
}

function Badge({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${className}`}>
      {children}
    </span>
  );
}

function FileUpload({ label, accept, onChange, loading, uploaded }) {
  const inputId = `file-${label.replace(/\s/g, '-').toLowerCase()}`;
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-slate-600">{label}</label>
      <div className="relative">
        <input id={inputId} type="file" accept={accept} onChange={onChange} className="hidden" disabled={loading} />
        <label htmlFor={inputId} className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed cursor-pointer transition-all ${uploaded ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-slate-300 hover:border-teal-400 hover:bg-teal-50 text-slate-600"}`}>
          {loading ? <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
            : uploaded ? <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            : <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>}
          <span className="font-medium">{uploaded ? "Uploaded" : "Choose File"}</span>
        </label>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function ContractorJobPage() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const token = params.get("token");
  const initialAction = params.get("action");
  const jobIdFromPath = window.location.pathname.split("/").filter(Boolean).pop();

  // States
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [jobData, setJobData] = useState(null);
  const [error, setError] = useState("");

  // Form states
  const [declineReason, setDeclineReason] = useState("");
  const [contractorNote, setContractorNote] = useState("");
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleNote, setScheduleNote] = useState("");
  const [quoteScope, setQuoteScope] = useState("");
  const [quoteNotes, setQuoteNotes] = useState("");
  const [lineItems, setLineItems] = useState([{ description: "", qty: 1, unit_cost: 0 }]);

  // Upload states
  const [uploading, setUploading] = useState({});
  const [certFile, setCertFile] = useState(null);

  // Location intelligence states
  const [nearbyJobs, setNearbyJobs] = useState([]);
  const [sameSiteJobs, setSameSiteJobs] = useState([]);
  const [completionChecklist, setCompletionChecklist] = useState(null);
  const [loadingNearby, setLoadingNearby] = useState(false);
  const [loadingSameSite, setLoadingSameSite] = useState(false);
  const [loadingChecklist, setLoadingChecklist] = useState(false);

  // RAMS (Risk Assessment & Method Statement) state
  const [ramsData, setRamsData] = useState(null);
  const [ramsAccepted, setRamsAccepted] = useState(false);
  const [loadingRams, setLoadingRams] = useState(false);
  const [acceptingRams, setAcceptingRams] = useState(false);
  
  // Photo upload tracking state
  const [photoStatus, setPhotoStatus] = useState({
    before: false,
    during: false,
    after: false
  });

  // Invoice submission state
  const [invoiceAmount, setInvoiceAmount] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceNotes, setInvoiceNotes] = useState("");
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [submittingInvoice, setSubmittingInvoice] = useState(false);
  const [invoiceError, setInvoiceError] = useState("");

  // Fetch job data - SAFE LOAD (no stacked errors)
  const fetchJob = useCallback(async () => {
    if (!token) {
      setError("Invalid access link - no token provided");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(""); // Clear previous errors
      const res = await axios.get(`${API}/api/contractor/job/${jobIdFromPath}?token=${encodeURIComponent(token)}`);
      
      // Safe response handling
      if (!res.data || (!res.data.ok && !res.data.success && !res.data.data && !res.data.job)) {
        setError("Unable to load job details");
        return;
      }

      const data = res.data.data || res.data;
      setJobData({
        job: data.job || data,
        contractor: data.contractor,
        quotes: data.quotes || [],
        notes: data.notes || [],
        whatsapp_link: data.whatsapp_link,
        rams_accepted: data.rams_accepted || res.data.rams_accepted || false,
        rams_accepted_at: data.rams_accepted_at || res.data.rams_accepted_at || null,
      });
      
      // Set initial RAMS status from job data
      setRamsAccepted(data.rams_accepted || res.data.rams_accepted || false);

      if (data.job?.scheduled_date) setScheduleDate(data.job.scheduled_date);
    } catch (err) {
      console.error("Fetch error:", err);
      // Single clean error - no stacking
      setError("Unable to load job. Please try refreshing.");
    } finally {
      setLoading(false);
    }
  }, [token, jobIdFromPath]);

  useEffect(() => { fetchJob(); }, [fetchJob]);

  // Auto-accept if action=accept
  useEffect(() => {
    if (initialAction === "accept" && jobData && !acting) handleRespond("accept");
  }, [initialAction, jobData]);

  // ============================================================================
  // ACTION HANDLERS
  // ============================================================================

  const handleRespond = async (action) => {
    if (action === "decline" && !declineReason.trim()) {
      toast.error("Please provide a reason for declining");
      return;
    }
    setActing(true);
    try {
      const res = await axios.post(`${API}/api/contractor/job/${jobIdFromPath}/respond?token=${encodeURIComponent(token)}`, {
        action,
        decline_reason: action === "decline" ? declineReason : undefined,
      });
      if (res.data.ok || res.data.success) {
        toast.success(action === "accept" ? "Job accepted!" : "Job declined");
        fetchJob();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} job`);
    } finally {
      setActing(false);
    }
  };

  const handleSchedule = async () => {
    if (!scheduleDate) { toast.error("Please select a date"); return; }
    setActing(true);
    try {
      const res = await axios.post(`${API}/api/contractor/job/${jobIdFromPath}/schedule?token=${encodeURIComponent(token)}`, { scheduled_date: scheduleDate, notes: scheduleNote });
      if (res.data.ok || res.data.success) { toast.success("Job scheduled successfully!"); fetchJob(); }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to schedule"); }
    finally { setActing(false); }
  };

  const handleConfirmAttendance = async () => {
    setActing(true);
    try {
      const res = await axios.post(`${API}/api/contractor/job/${jobIdFromPath}/confirm-attendance?token=${encodeURIComponent(token)}`, {});
      if (res.data.ok || res.data.success) { toast.success("Attendance confirmed - thank you!"); fetchJob(); }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to confirm attendance"); }
    finally { setActing(false); }
  };

  const handleAddNote = async () => {
    if (!contractorNote.trim()) { toast.error("Please enter a note"); return; }
    setActing(true);
    try {
      const res = await axios.post(`${API}/api/contractor/job/${jobIdFromPath}/note?token=${encodeURIComponent(token)}`, { note: contractorNote });
      if (res.data.ok || res.data.success) { toast.success("Note added"); setContractorNote(""); fetchJob(); }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add note"); }
    finally { setActing(false); }
  };

  const handleSubmitQuote = async () => {
    if (!quoteScope.trim()) { toast.error("Please enter scope of works"); return; }
    if (lineItems.length === 0 || !lineItems.some(li => li.description && li.unit_cost > 0)) {
      toast.error("Please add at least one line item"); return;
    }
    setActing(true);
    try {
      const validItems = lineItems.filter(li => li.description.trim() && li.unit_cost > 0);
      const subtotal = validItems.reduce((sum, li) => sum + safeNumber(li.qty) * safeNumber(li.unit_cost), 0);
      const vat = subtotal * 0.2;
      const total = subtotal + vat;

      const res = await axios.post(`${API}/api/contractor/job/${jobIdFromPath}/quote?token=${encodeURIComponent(token)}`, {
        scope_of_works: quoteScope,
        line_items: validItems.map(li => ({ description: li.description, quantity: safeNumber(li.qty), unit_cost: safeNumber(li.unit_cost), line_total: safeNumber(li.qty) * safeNumber(li.unit_cost) })),
        subtotal_ex_vat: subtotal, vat_amount: vat, total_inc_vat: total, notes: quoteNotes,
      });
      if (res.data.ok || res.data.success) {
        toast.success("Quote submitted successfully!");
        setQuoteScope(""); setQuoteNotes(""); setLineItems([{ description: "", qty: 1, unit_cost: 0 }]);
        fetchJob();
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to submit quote"); }
    finally { setActing(false); }
  };

  const handleUploadDoc = async (fileType, file) => {
    if (!file) return;
    setUploading(prev => ({ ...prev, [fileType]: true }));
    try {
      const formData = new FormData();
      formData.append("file_type", fileType);
      formData.append("file", file);
      const res = await axios.post(`${API}/api/contractor/job/${jobIdFromPath}/upload?token=${encodeURIComponent(token)}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.data.ok || res.data.success) { 
        toast.success(`${fileType.replace("_", " ")} uploaded`); 
        
        // Update photo tracking status
        if (fileType === 'before_photos' || fileType === 'before') {
          setPhotoStatus(prev => ({ ...prev, before: true }));
        } else if (fileType === 'during_photos' || fileType === 'during') {
          setPhotoStatus(prev => ({ ...prev, during: true }));
        } else if (fileType === 'after_photos' || fileType === 'after') {
          setPhotoStatus(prev => ({ ...prev, after: true }));
        }
        
        fetchJob();
        fetchCompletionChecklist(); // Refresh checklist after upload
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(prev => ({ ...prev, [fileType]: false })); }
  };

  const handleUploadCertificate = async () => {
    if (!certFile) { toast.error("Please select a certificate file"); return; }
    setUploading(prev => ({ ...prev, certificate: true }));
    try {
      const formData = new FormData();
      formData.append("certificate_file", certFile);
      formData.append("token", token);
      const res = await axios.post(`${API}/api/contractor/job/upload-certificate`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.data.ok || res.data.success) { 
        toast.success("Certificate uploaded!"); 
        setCertFile(null); 
        fetchJob(); 
        fetchCompletionChecklist(); // Refresh checklist after certificate upload
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Certificate upload failed"); }
    finally { setUploading(prev => ({ ...prev, certificate: false })); }
  };

  // Line item handlers
  const addLineItem = () => setLineItems([...lineItems, { description: "", qty: 1, unit_cost: 0 }]);
  const removeLineItem = (idx) => setLineItems(lineItems.filter((_, i) => i !== idx));
  const updateLineItem = (idx, field, value) => {
    const updated = [...lineItems];
    updated[idx][field] = field === "description" ? value : safeNumber(value);
    setLineItems(updated);
  };

  const quoteSubtotal = lineItems.reduce((sum, li) => sum + safeNumber(li.qty) * safeNumber(li.unit_cost), 0);
  const quoteVat = quoteSubtotal * 0.2;
  const quoteTotal = quoteSubtotal + quoteVat;

  // ============================================================================
  // LOCATION INTELLIGENCE HANDLERS
  // ============================================================================

  const fetchNearbyJobs = useCallback(async () => {
    if (!token || !jobIdFromPath) return;
    setLoadingNearby(true);
    try {
      const res = await axios.get(`${API}/api/contractor/job/${jobIdFromPath}/nearby?token=${encodeURIComponent(token)}`);
      if (res.data.ok) {
        setNearbyJobs(res.data.nearby_jobs || []);
      }
    } catch (err) {
      console.error("Failed to fetch nearby jobs:", err);
    } finally {
      setLoadingNearby(false);
    }
  }, [token, jobIdFromPath]);

  const fetchSameSiteJobs = useCallback(async () => {
    if (!token || !jobIdFromPath) return;
    setLoadingSameSite(true);
    try {
      const res = await axios.get(`${API}/api/contractor/job/${jobIdFromPath}/same-site?token=${encodeURIComponent(token)}`);
      if (res.data.ok) {
        setSameSiteJobs(res.data.same_site_jobs || []);
      }
    } catch (err) {
      console.error("Failed to fetch same site jobs:", err);
    } finally {
      setLoadingSameSite(false);
    }
  }, [token, jobIdFromPath]);

  const fetchCompletionChecklist = useCallback(async () => {
    if (!token || !jobIdFromPath) return;
    setLoadingChecklist(true);
    try {
      const res = await axios.get(`${API}/api/contractor/job/${jobIdFromPath}/completion-checklist?token=${encodeURIComponent(token)}`);
      if (res.data.ok) {
        setCompletionChecklist(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch checklist:", err);
    } finally {
      setLoadingChecklist(false);
    }
  }, [token, jobIdFromPath]);

  // Load location intelligence when job data loads
  useEffect(() => {
    if (jobData?.job) {
      fetchNearbyJobs();
      fetchSameSiteJobs();
      fetchCompletionChecklist();
      fetchRams();
    }
  }, [jobData?.job?.id, fetchNearbyJobs, fetchSameSiteJobs, fetchCompletionChecklist]);

  // ============================================================================
  // RAMS (RISK ASSESSMENT & METHOD STATEMENT) HANDLERS
  // ============================================================================

  const fetchRams = useCallback(async () => {
    if (!token || !jobIdFromPath) return;
    setLoadingRams(true);
    try {
      const res = await axios.get(`${API}/api/contractor/job/${jobIdFromPath}/rams?token=${encodeURIComponent(token)}`);
      if (res.data.ok || res.data.success) {
        setRamsData(res.data.rams);
        setRamsAccepted(res.data.accepted || false);
      }
    } catch (err) {
      console.error("Failed to fetch RAMS:", err);
    } finally {
      setLoadingRams(false);
    }
  }, [token, jobIdFromPath]);

  const handleAcceptRams = async () => {
    setAcceptingRams(true);
    try {
      const res = await axios.post(`${API}/api/contractor/job/${jobIdFromPath}/accept-rams?token=${encodeURIComponent(token)}`);
      if (res.data.ok || res.data.success) {
        setRamsAccepted(true);
        toast.success("RAMS accepted successfully! You can now start the job.");
        fetchJob(); // Refresh job data
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept RAMS");
    } finally {
      setAcceptingRams(false);
    }
  };

  const handleCompleteJob = async () => {
    // Check RAMS first
    if (!ramsAccepted) {
      toast.error("You must accept the RAMS before completing the job.");
      return;
    }
    if (!completionChecklist?.can_complete) {
      toast.error(`Cannot complete job. Missing: ${completionChecklist?.missing_items?.join(', ')}`);
      return;
    }
    setActing(true);
    try {
      const res = await axios.post(`${API}/api/contractor/job/${jobIdFromPath}/complete?token=${encodeURIComponent(token)}`);
      if (res.data.ok || res.data.success) {
        toast.success("Job completed successfully!");
        fetchJob();
        fetchCompletionChecklist();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to complete job");
    } finally {
      setActing(false);
    }
  };

  // ============================================================================
  // INVOICE SUBMISSION HANDLER
  // ============================================================================

  const handleSubmitInvoice = async () => {
    setInvoiceError("");
    
    if (!invoiceAmount || parseFloat(invoiceAmount) <= 0) {
      setInvoiceError("Please enter a valid invoice amount");
      return;
    }
    
    setSubmittingInvoice(true);
    try {
      const res = await axios.post(
        `${API}/api/contractor/job/${jobIdFromPath}/submit-invoice?token=${encodeURIComponent(token)}`,
        null,
        {
          params: {
            amount: parseFloat(invoiceAmount),
            invoice_number: invoiceNumber || undefined,
            notes: invoiceNotes || undefined
          }
        }
      );
      
      if (res.data.ok || res.data.success) {
        toast.success("Invoice submitted successfully!");
        setShowInvoiceForm(false);
        setInvoiceAmount("");
        setInvoiceNumber("");
        setInvoiceNotes("");
        fetchJob(); // Refresh job data
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Failed to submit invoice";
      setInvoiceError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSubmittingInvoice(false);
    }
  };

  const openInMaps = () => {
    const job = jobData?.job;
    if (!job) return;
    const query = encodeURIComponent(`${job.site_name || job.park_name || ''}, ${job.postcode || ''}`);
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
  };

  // ============================================================================
  // LOADING & ERROR STATES
  // ============================================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-10 w-10 text-teal-600 mx-auto mb-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-slate-600">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Access Error</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button variant="secondary" onClick={() => window.location.reload()}>Try Again</Button>
          </div>
        </Card>
      </div>
    );
  }

  // ============================================================================
  // DERIVED STATE
  // ============================================================================

  const job = jobData?.job;
  const quotes = jobData?.quotes || [];
  const hasAccepted = job?.contractor_status === "Accepted" || job?.status === "Scheduled" || job?.status === "Approved" || job?.stage === "Scheduled";
  const hasSubmittedQuote = quotes.length > 0;
  const showAcceptDecline = !hasAccepted && job?.status !== "Completed" && job?.status !== "Closed";

  // Map job to unified format for JobCard
  const unifiedJob = job ? {
    job_id: job.id || job.job_id || job._id,
    job_reference: job.job_reference,
    park_name: job.park_name || job.site_name,
    unit_name: job.unit_name || job.asset_name || job.plot_number,
    full_address: job.full_address || `${job.park_name || job.site_name || ''}, ${job.unit_name || job.asset_name || ''}`.replace(/^, |, $/g, ''),
    postcode: job.postcode,
    contact_name: job.contact_name || job.reported_by,
    contact_phone: job.contact_phone || job.reporter_phone,
    contact_email: job.contact_email || job.reporter_email,
    job_type: job.job_type,
    trade: job.trade,
    priority: job.priority,
    description: job.description,
    access_notes: job.access_notes || job.access_instructions,
    created_date: job.created_at || job.created_date,
    scheduled_date: job.scheduled_date,
    due_date: job.due_date || job.sla_deadline,
    stage: job.stage || job.status,
    status: job.status || job.stage,
  } : null;

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contractor-job-page">
      
      {/* Header */}
      <header className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-4">
        <div className="max-w-4xl mx-auto px-4 flex items-center gap-3">
          <img src="https://customer-assets.emergentagent.com/job_park-case-tracker/artifacts/cww8tyf2_LHS%20LOGO%202%20.png" alt="LHS" className="h-10" />
          <span className="text-lg font-semibold">Contractor Portal</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">

        {/* JobCard - Unified Display */}
        <JobCard
          job={unifiedJob}
          variant="contractor"
          actions={{
            onAccept: showAcceptDecline ? () => handleRespond("accept") : undefined,
            onDecline: showAcceptDecline ? () => {
              if (!declineReason.trim()) {
                toast.error("Please enter a decline reason first");
                document.getElementById("decline-reason-input")?.focus();
                return;
              }
              handleRespond("decline");
            } : undefined,
            onSchedule: hasAccepted && !job?.scheduled_date ? () => {
              document.getElementById("schedule-section")?.scrollIntoView({ behavior: "smooth" });
            } : undefined,
            onQuote: !hasSubmittedQuote ? () => {
              document.getElementById("quote-section")?.scrollIntoView({ behavior: "smooth" });
            } : undefined,
            onDownloadPdf: () => window.open(`${API}/api/fm/jobs/${job?.id || job?.job_id}/job-sheet.pdf`, "_blank"),
            onWhatsApp: jobData?.whatsapp_link ? () => window.open(jobData.whatsapp_link, "_blank") : undefined,
          }}
        />

        {/* ================================================================
            ELITE CONTRACTOR HEADER - All critical info visible instantly
        ================================================================ */}
        <Card className="p-5 bg-white">
          <div className="space-y-3">
            {/* Park + Job ID */}
            <div className="flex justify-between items-start">
              <div className="text-lg font-bold text-slate-800">{job?.park_name || job?.park || 'N/A'}</div>
              <div className="text-xs bg-slate-100 px-2.5 py-1 rounded-lg font-mono text-slate-600">
                {job?.job_reference || job?.id}
              </div>
            </div>
            
            {/* Plot / Unit */}
            <div className="text-sm text-slate-600">
              <span className="font-medium">Plot:</span> {job?.unit_name || job?.unit || 'N/A'}
            </div>
            
            {/* Address */}
            {(job?.address || job?.site_address) && (
              <div className="text-sm text-slate-600">{job?.address || job?.site_address}</div>
            )}
            
            {/* Due Date + Job Type */}
            <div className="flex justify-between items-center">
              <span className={`text-sm font-semibold ${job?.is_overdue ? 'text-red-600' : 'text-amber-600'}`}>
                Due: {job?.due_date || job?.response_due_date || 'N/A'}
              </span>
              <span className="bg-blue-100 text-blue-700 px-2.5 py-1 rounded-lg text-xs font-medium">
                {job?.job_type || job?.type || 'FM Job'}
              </span>
            </div>
            
            {/* Contact */}
            {(job?.contact_name || job?.contact_phone) && (
              <div className="text-sm text-slate-600">
                <span className="font-medium">Contact:</span> {job?.contact_name || 'N/A'}
                {job?.contact_phone && <span className="text-slate-400 ml-2">({job.contact_phone})</span>}
              </div>
            )}
          </div>
        </Card>

        {/* ================================================================
            ELITE ACTION CENTRE - Clean, fast, field-ready
        ================================================================ */}
        <Card className="p-5 bg-white">
          <div className="space-y-4">
            {/* Last Action indicator */}
            <div className="text-xs text-slate-500">
              Last Action: {job?.last_action || job?.last_update || 'None recorded'}
            </div>
            
            {/* Primary Action Grid */}
            <div className="grid grid-cols-2 gap-3">
              {/* WhatsApp Contractor - ALWAYS contractor first */}
              <button
                onClick={async () => {
                  // First try backend API for proper logging
                  try {
                    if (jobData?.whatsapp_link) {
                      window.open(jobData.whatsapp_link, '_blank');
                      toast.success('WhatsApp opened');
                      return;
                    }
                  } catch (e) {}
                  toast.error('No contractor mobile available');
                }}
                disabled={!jobData?.whatsapp_link}
                className={`rounded-2xl p-4 text-white font-semibold text-sm transition-all ${
                  jobData?.whatsapp_link 
                    ? 'bg-green-500 hover:bg-green-600 active:scale-95' 
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
                data-testid="action-whatsapp-contractor"
              >
                <svg className="h-5 w-5 mx-auto mb-1" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp Contractor
              </button>
              
              {/* Call Contractor - use contractor phone from jobData.contractor */}
              <a
                href={(jobData?.contractor?.phone || jobData?.contractor?.mobile) ? `tel:${jobData?.contractor?.phone || jobData?.contractor?.mobile}` : '#'}
                onClick={(e) => {
                  if (!(jobData?.contractor?.phone || jobData?.contractor?.mobile)) {
                    e.preventDefault();
                    toast.error('No contractor phone available');
                  }
                }}
                className={`rounded-2xl p-4 text-white font-semibold text-sm text-center transition-all ${
                  (jobData?.contractor?.phone || jobData?.contractor?.mobile)
                    ? 'bg-blue-500 hover:bg-blue-600 active:scale-95' 
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
                data-testid="action-call-contractor"
              >
                <svg className="h-5 w-5 mx-auto mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                Call Contractor
              </a>
              
              {/* Request Quote */}
              {!hasSubmittedQuote && (
                <button
                  onClick={() => {
                    document.getElementById("quote-section")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="rounded-2xl p-4 bg-purple-500 hover:bg-purple-600 text-white font-semibold text-sm transition-all active:scale-95"
                  data-testid="action-request-quote"
                >
                  <svg className="h-5 w-5 mx-auto mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  Submit Quote
                </button>
              )}
              
              {/* Open in Maps */}
              <button
                onClick={() => {
                  const address = encodeURIComponent(
                    job?.address || job?.site_address || `${job?.park_name} ${job?.unit_name}` || ''
                  );
                  if (address) {
                    window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank');
                  } else {
                    toast.error('No address available for maps');
                  }
                }}
                className="rounded-2xl p-4 bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm transition-all active:scale-95"
                data-testid="action-open-maps"
              >
                <svg className="h-5 w-5 mx-auto mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                Open in Maps
              </button>
            </div>
          </div>
        </Card>

        {/* Site Contact Quick Actions - WhatsApp / Call */}
        {(job?.contact_phone || job?.contact_name) && (
          <Card className="p-5 bg-gradient-to-r from-teal-50 to-emerald-50">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-teal-800">Site Contact</div>
              {job?.contact_name && <span className="text-sm text-teal-700">{job.contact_name}</span>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {job?.contact_phone && (
                <>
                  <button
                    onClick={async () => {
                      try {
                        const res = await axios.post(
                          `${API}/api/contractor/job/${job.id || job.job_id}/actions/site-contact?token=${encodeURIComponent(token)}&channel=whatsapp`
                        );
                        if (res.data.ok && res.data.whatsapp_url) {
                          window.open(res.data.whatsapp_url, '_blank');
                          toast.success('WhatsApp opened for site contact');
                        }
                      } catch (err) {
                        const phone = job.contact_phone.replace(/\s/g, '').replace(/^\+?44/, '44').replace(/^0/, '44');
                        const message = encodeURIComponent(`Hi, regarding job ${job.job_reference || job.id}.`);
                        window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
                      }
                    }}
                    className="rounded-xl p-3 bg-green-500 hover:bg-green-600 text-white font-semibold text-sm transition-all active:scale-95"
                    data-testid="whatsapp-site-btn"
                  >
                    <svg className="h-4 w-4 inline mr-1" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WhatsApp Site
                  </button>
                  <a
                    href={`tel:${job.contact_phone}`}
                    className="rounded-xl p-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold text-sm text-center transition-all active:scale-95"
                    data-testid="call-site-btn"
                  >
                    <svg className="h-4 w-4 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                    Call Site
                  </a>
                </>
              )}
            </div>
            {job?.contact_phone && (
              <p className="text-xs text-teal-600 mt-2">{job.contact_phone}</p>
            )}
          </Card>
        )}

        {/* ================================================================
            ACTION CHECKLIST - CRITICAL REQUIREMENTS TRACKER
        ================================================================ */}
        {hasAccepted && job?.status !== 'Completed' && (
          <Card className="p-5 bg-white border-2 border-slate-200" data-testid="action-checklist">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">Completion Checklist</h3>
              <span className="text-xs bg-slate-100 px-3 py-1 rounded-full text-slate-600">
                {[photoStatus.before, photoStatus.during, photoStatus.after, 
                  !(job?.requires_certificate || job?.certificate_required) || job?.certificate_uploaded,
                  ramsAccepted].filter(Boolean).length}/5 Complete
              </span>
            </div>
            
            <div className="space-y-3">
              {/* Before Photo */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    photoStatus.before || job?.has_before_photo ? 'bg-emerald-100' : 'bg-red-100'
                  }`}>
                    {photoStatus.before || job?.has_before_photo ? (
                      <svg className="h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </div>
                  <span className="font-medium text-slate-800">Before Photo</span>
                </div>
                {!(photoStatus.before || job?.has_before_photo) && (
                  <label className="px-3 py-1.5 bg-teal-600 text-white text-sm font-medium rounded-full cursor-pointer hover:bg-teal-700 transition-colors">
                    Upload
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUploadDoc("before_photos", e.target.files[0])} />
                  </label>
                )}
              </div>

              {/* During Photo */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    photoStatus.during || job?.has_during_photo ? 'bg-emerald-100' : 'bg-red-100'
                  }`}>
                    {photoStatus.during || job?.has_during_photo ? (
                      <svg className="h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </div>
                  <span className="font-medium text-slate-800">During Photo</span>
                </div>
                {!(photoStatus.during || job?.has_during_photo) && (
                  <label className="px-3 py-1.5 bg-teal-600 text-white text-sm font-medium rounded-full cursor-pointer hover:bg-teal-700 transition-colors">
                    Upload
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUploadDoc("during_photos", e.target.files[0])} />
                  </label>
                )}
              </div>

              {/* After Photo */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    photoStatus.after || job?.has_after_photo ? 'bg-emerald-100' : 'bg-red-100'
                  }`}>
                    {photoStatus.after || job?.has_after_photo ? (
                      <svg className="h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </div>
                  <span className="font-medium text-slate-800">After Photo</span>
                </div>
                {!(photoStatus.after || job?.has_after_photo) && (
                  <label className="px-3 py-1.5 bg-teal-600 text-white text-sm font-medium rounded-full cursor-pointer hover:bg-teal-700 transition-colors">
                    Upload
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUploadDoc("after_photos", e.target.files[0])} />
                  </label>
                )}
              </div>

              {/* Certificate (if required) */}
              {(job?.requires_certificate || job?.certificate_required) && (
                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      job?.certificate_uploaded ? 'bg-emerald-100' : 'bg-amber-100'
                    }`}>
                      {job?.certificate_uploaded ? (
                        <svg className="h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      )}
                    </div>
                    <span className="font-medium text-slate-800">Certificate</span>
                  </div>
                  {!job?.certificate_uploaded && (
                    <label className="px-3 py-1.5 bg-amber-500 text-white text-sm font-medium rounded-full cursor-pointer hover:bg-amber-600 transition-colors">
                      Upload
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => {
                        setCertFile(e.target.files[0]);
                        handleUploadCertificate();
                      }} />
                    </label>
                  )}
                </div>
              )}

              {/* RAMS */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    ramsAccepted ? 'bg-emerald-100' : 'bg-blue-100'
                  }`}>
                    {ramsAccepted ? (
                      <svg className="h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                  </div>
                  <span className="font-medium text-slate-800">RAMS Accepted</span>
                </div>
                {!ramsAccepted && (
                  <Button
                    onClick={() => {
                      const ramsSection = document.querySelector('[data-testid="rams-section"]');
                      if (ramsSection) {
                        ramsSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    variant="outline"
                    size="sm"
                    className="rounded-full text-blue-600 border-blue-300 hover:bg-blue-50"
                  >
                    Accept RAMS
                  </Button>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* ================================================================
            RAMS (RISK ASSESSMENT & METHOD STATEMENT) - MANDATORY BEFORE START
        ================================================================ */}
        {hasAccepted && !ramsAccepted && job?.status !== 'Completed' && (
          <Card className="p-5 border-2 border-yellow-300 bg-gradient-to-br from-yellow-50 to-amber-50" data-testid="rams-section">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 p-3 bg-yellow-100 rounded-xl">
                <svg className="h-7 w-7 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-900 mb-2">RAMS Required</h3>
                <p className="text-sm text-slate-600 mb-4">Please review and accept the Risk Assessment & Method Statement before starting work on this job.</p>
                
                {loadingRams ? (
                  <div className="flex items-center gap-2 text-slate-500">
                    <div className="animate-spin h-4 w-4 border-2 border-yellow-500 border-t-transparent rounded-full"></div>
                    <span className="text-sm">Loading RAMS...</span>
                  </div>
                ) : ramsData ? (
                  <div className="space-y-4">
                    <div className="bg-white border border-yellow-200 rounded-xl p-4">
                      <h4 className="font-semibold text-slate-800 mb-2">{ramsData.title}</h4>
                      <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
                        {ramsData.content}
                      </pre>
                      {ramsData.version && (
                        <p className="text-xs text-slate-400 mt-3">Version: {ramsData.version}</p>
                      )}
                    </div>
                    
                    <Button
                      onClick={handleAcceptRams}
                      loading={acceptingRams}
                      className="w-full bg-yellow-500 hover:bg-yellow-600 text-slate-900 font-bold py-4"
                      data-testid="accept-rams-btn"
                    >
                      <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      I Have Read and Accept RAMS
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Unable to load RAMS document.</p>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* RAMS Accepted Confirmation Badge */}
        {hasAccepted && ramsAccepted && job?.status !== 'Completed' && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center gap-3" data-testid="rams-accepted-badge">
            <svg className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="font-medium text-emerald-800">RAMS Accepted</p>
              <p className="text-sm text-emerald-600">You can now proceed with the job.</p>
            </div>
          </div>
        )}

        {/* Decline Reason Input (shown when accept/decline available) */}
        {showAcceptDecline && (
          <Card className="p-5">
            <Input 
              id="decline-reason-input"
              label="Decline Reason (required if declining)" 
              placeholder="Why are you declining this job?" 
              value={declineReason} 
              onChange={(e) => setDeclineReason(e.target.value)} 
            />
          </Card>
        )}

        {/* Schedule Section - BLOCKED if RAMS not accepted */}
        {hasAccepted && !job?.scheduled_date && (
          <Card id="schedule-section" className="p-5">
            <div className="text-lg font-semibold text-slate-900 mb-4">Schedule Visit</div>
            {!ramsAccepted ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
                <svg className="h-8 w-8 text-yellow-500 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H10m12-6a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm font-medium text-yellow-800">Accept RAMS First</p>
                <p className="text-xs text-yellow-600 mt-1">You must accept the Risk Assessment before scheduling.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <Input type="date" label="Visit Date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} data-testid="schedule-date-input" />
                <Textarea label="Scheduling Notes (optional)" placeholder="Any notes about the visit..." value={scheduleNote} onChange={(e) => setScheduleNote(e.target.value)} />
                <Button onClick={handleSchedule} loading={acting} data-testid="confirm-schedule-btn">Confirm Schedule</Button>
              </div>
            )}
          </Card>
        )}

        {/* Confirm Attendance - BLOCKED if RAMS not accepted */}
        {job?.scheduled_date && !job?.attendance_confirmed_at && !job?.completed_at && job?.status !== 'Completed' && (
          <Card className="p-5">
            <div className="text-lg font-semibold text-slate-900 mb-4">On-Site? Confirm Arrival</div>
            {!ramsAccepted ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
                <p className="text-sm font-medium text-yellow-800">Accept RAMS First</p>
                <p className="text-xs text-yellow-600 mt-1">You must accept the Risk Assessment before confirming attendance.</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-slate-600 mb-4">Click below when you arrive on-site to confirm your attendance.</p>
                <Button onClick={handleConfirmAttendance} loading={acting} variant="primary" data-testid="confirm-attendance-btn">
                  I'm On Site - Confirm Attendance
                </Button>
              </>
            )}
          </Card>
        )}

        {job?.attendance_confirmed_at && !job?.completed_at && job?.status !== 'Completed' && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center gap-3">
            <svg className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <div><p className="font-medium text-emerald-800">Attendance Confirmed</p><p className="text-sm text-emerald-600">Complete the job when work is finished.</p></div>
          </div>
        )}

        {/* Add Note */}
        <Card className="p-5">
          <div className="text-lg font-semibold text-slate-900 mb-4">Add Note</div>
          <div className="space-y-4">
            <Textarea placeholder="Add a note for this job..." value={contractorNote} onChange={(e) => setContractorNote(e.target.value)} data-testid="note-textarea" />
            <Button variant="secondary" onClick={handleAddNote} loading={acting} disabled={!contractorNote.trim()} data-testid="save-note-btn">Save Note</Button>
          </div>
        </Card>

        {/* Quote Builder */}
        {!hasSubmittedQuote && (
          <Card id="quote-section" className="p-5">
            <div className="text-lg font-semibold text-slate-900 mb-4">Submit Quote</div>
            <div className="space-y-4">
              <Textarea label="Scope of Works" placeholder="Describe the work to be carried out..." value={quoteScope} onChange={(e) => setQuoteScope(e.target.value)} data-testid="quote-scope-textarea" />
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-slate-600">Line Items (Ex VAT)</label>
                  <Button variant="outline" onClick={addLineItem} className="text-sm px-3 py-1.5">+ Add Item</Button>
                </div>
                <div className="space-y-3">
                  {lineItems.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-5"><input className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" placeholder="Description" value={item.description} onChange={(e) => updateLineItem(idx, "description", e.target.value)} /></div>
                      <div className="col-span-2"><input type="number" className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm text-center" placeholder="Qty" value={item.qty} onChange={(e) => updateLineItem(idx, "qty", e.target.value)} /></div>
                      <div className="col-span-3"><div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">£</span><input type="number" step="0.01" className="w-full px-3 py-2 pl-6 rounded-xl border border-slate-200 text-sm text-right" placeholder="0.00" value={item.unit_cost || ""} onChange={(e) => updateLineItem(idx, "unit_cost", e.target.value)} /></div></div>
                      <div className="col-span-2 flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-700">£{(safeNumber(item.qty) * safeNumber(item.unit_cost)).toFixed(2)}</span>
                        {lineItems.length > 1 && <button onClick={() => removeLineItem(idx)} className="text-red-400 hover:text-red-600 p-1"><svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-slate-600"><span>Subtotal (Ex VAT)</span><span className="font-medium">£{quoteSubtotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-slate-600"><span>VAT (20%)</span><span className="font-medium">£{quoteVat.toFixed(2)}</span></div>
                <div className="flex justify-between text-lg font-bold text-teal-600 pt-2 border-t border-slate-200"><span>Total (Inc VAT)</span><span>£{quoteTotal.toFixed(2)}</span></div>
              </div>
              <Textarea label="Quote Notes (optional)" placeholder="Any additional notes..." value={quoteNotes} onChange={(e) => setQuoteNotes(e.target.value)} />
              <Button onClick={handleSubmitQuote} loading={acting} className="w-full" data-testid="submit-quote-btn">Submit Quote</Button>
            </div>
          </Card>
        )}

        {hasSubmittedQuote && (
          <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="text-lg font-semibold text-slate-900">Quote Submitted</div>
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">{quotes[0]?.status || "Submitted"}</span>
            </div>
            <div className="bg-emerald-50 rounded-xl p-4 text-center">
              <p className="text-emerald-700 font-medium">Quote Total: £{safeNumber(quotes[0]?.total_cost || quotes[0]?.total_inc_vat).toFixed(2)}</p>
            </div>
          </Card>
        )}

        {/* Document Uploads */}
        <Card className="p-5">
          <div className="text-lg font-semibold text-slate-900 mb-4">Upload Documents</div>
          <div className="grid grid-cols-2 gap-4">
            <FileUpload label="Job Sheet" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={(e) => handleUploadDoc("job_sheet", e.target.files[0])} loading={uploading.job_sheet} uploaded={job?.job_sheet_uploaded} />
            <FileUpload label="Before Photo" accept="image/*" onChange={(e) => handleUploadDoc("before_photos", e.target.files[0])} loading={uploading.before_photos} uploaded={job?.before_photos_uploaded} />
            <FileUpload label="During Photo" accept="image/*" onChange={(e) => handleUploadDoc("during_photos", e.target.files[0])} loading={uploading.during_photos} uploaded={job?.during_photos_uploaded} />
            <FileUpload label="After Photo" accept="image/*" onChange={(e) => handleUploadDoc("after_photos", e.target.files[0])} loading={uploading.after_photos} uploaded={job?.after_photos_uploaded} />
          </div>
        </Card>

        {/* Certificate Upload */}
        {job?.requires_certificate && (
          <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="text-lg font-semibold text-slate-900">Upload Certificate</div>
              {job?.mandatory_certificate_satisfied && <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">Uploaded</span>}
            </div>
            <p className="text-sm text-slate-600 mb-4">This job requires a <strong>{job?.certificate_type_required || "certificate"}</strong> before completion.</p>
            <div className="space-y-4">
              <FileUpload label="Certificate File" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setCertFile(e.target.files[0])} loading={uploading.certificate} uploaded={job?.mandatory_certificate_satisfied} />
              {certFile && <Button onClick={handleUploadCertificate} loading={uploading.certificate}>Upload Certificate</Button>}
            </div>
          </Card>
        )}

        {/* ============================================================ */}
        {/* LOCATION INTELLIGENCE + COMPLETION ENFORCEMENT              */}
        {/* ============================================================ */}

        {/* Open in Maps Button */}
        <Card className="p-4">
          <Button 
            onClick={openInMaps} 
            variant="secondary" 
            className="w-full"
            data-testid="open-maps-btn"
          >
            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Open in Google Maps
          </Button>
        </Card>

        {/* ================================================================
            COMPLETION ENFORCEMENT UI - Photos + Certs Required
        ================================================================ */}
        {job?.status !== 'Completed' && (
          <Card className="p-5 border-2 border-amber-200 bg-gradient-to-br from-amber-50/50 to-white" id="complete-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-xl ${completionChecklist?.can_complete ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                  {completionChecklist?.can_complete ? (
                    <svg className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <svg className="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Complete Job</h3>
                  <p className="text-xs text-slate-500">All required items must be uploaded</p>
                </div>
              </div>
              {completionChecklist && (
                <div className="text-right">
                  <span className={`inline-block px-4 py-2 rounded-xl text-sm font-bold ${
                    completionChecklist.can_complete 
                      ? 'bg-emerald-500 text-white' 
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {completionChecklist.completion_percentage}% Ready
                  </span>
                </div>
              )}
            </div>

            {loadingChecklist ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin h-6 w-6 border-2 border-amber-500 border-t-transparent rounded-full" />
              </div>
            ) : completionChecklist ? (
              <div className="space-y-4">
                {/* Progress Bar */}
                <div className="relative h-3 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className={`absolute inset-y-0 left-0 rounded-full transition-all duration-500 ${
                      completionChecklist.can_complete ? 'bg-emerald-500' : 'bg-amber-500'
                    }`}
                    style={{ width: `${completionChecklist.completion_percentage}%` }}
                  />
                </div>
                
                {/* Checklist Items */}
                <div className="grid gap-3">
                  {completionChecklist.checklist?.map((item) => (
                    <div 
                      key={item.id} 
                      className={`flex items-center gap-4 p-4 rounded-2xl border-2 transition-all ${
                        item.completed 
                          ? 'bg-emerald-50 border-emerald-200' 
                          : item.required 
                            ? 'bg-red-50 border-red-300 shadow-sm' 
                            : 'bg-slate-50 border-slate-200'
                      }`}
                    >
                      {/* Status Icon */}
                      <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
                        item.completed 
                          ? 'bg-emerald-500' 
                          : item.required 
                            ? 'bg-red-100 border-2 border-red-300' 
                            : 'bg-slate-200'
                      }`}>
                        {item.completed ? (
                          <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : item.type === 'photo' ? (
                          <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        ) : item.type === 'certificate' ? (
                          <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                        )}
                      </div>
                      
                      {/* Item Details */}
                      <div className="flex-1 min-w-0">
                        <p className={`font-semibold ${
                          item.completed ? 'text-emerald-700' : item.required ? 'text-red-700' : 'text-slate-600'
                        }`}>
                          {item.label}
                          {item.required && !item.completed && (
                            <span className="ml-2 text-xs font-normal bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                              REQUIRED
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>
                      </div>
                      
                      {/* Upload Action (if not completed) */}
                      {!item.completed && item.type === 'photo' && (
                        <button 
                          className="flex-shrink-0 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold rounded-xl transition-all active:scale-95"
                          onClick={() => {
                            const photoType = item.id.replace('photo_', '');
                            document.getElementById(`photo-upload-${photoType}`)?.scrollIntoView({ behavior: 'smooth' });
                          }}
                          data-testid={`upload-${item.id}-btn`}
                        >
                          Upload
                        </button>
                      )}
                      {!item.completed && item.type === 'certificate' && (
                        <button 
                          className="flex-shrink-0 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white text-sm font-semibold rounded-xl transition-all active:scale-95"
                          onClick={() => {
                            document.getElementById('certificate-upload')?.scrollIntoView({ behavior: 'smooth' });
                          }}
                          data-testid="upload-certificate-btn"
                        >
                          Upload Cert
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Complete Button or Warning */}
                <div className="mt-6">
                  {/* RAMS Check First */}
                  {!ramsAccepted && (
                    <div className="bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-5 mb-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 p-2 bg-yellow-100 rounded-lg">
                          <svg className="h-5 w-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-bold text-yellow-800">RAMS Not Accepted</p>
                          <p className="text-sm text-yellow-600 mt-1">
                            You must accept the Risk Assessment & Method Statement before completing this job.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {ramsAccepted && completionChecklist.can_complete ? (
                    <Button 
                      onClick={handleCompleteJob} 
                      variant="success" 
                      className="w-full py-4 text-lg"
                      loading={acting}
                      data-testid="complete-job-btn"
                    >
                      <svg className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Mark Job as Complete
                    </Button>
                  ) : ramsAccepted && !completionChecklist.can_complete ? (
                    <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-5">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 p-2 bg-red-100 rounded-lg">
                          <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-bold text-red-800">Cannot Complete Job Yet</p>
                          <p className="text-sm text-red-600 mt-1">
                            Please upload the following before completing:
                          </p>
                          <ul className="mt-2 space-y-1">
                            {completionChecklist.missing_items?.map((item, idx) => (
                              <li key={idx} className="text-sm text-red-700 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-red-400 rounded-full"></span>
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Button 
                      disabled={true}
                      variant="secondary" 
                      className="w-full py-4 text-lg opacity-50 cursor-not-allowed"
                      data-testid="complete-job-btn-disabled"
                    >
                      Complete Job (Accept RAMS First)
                    </Button>
                  )}
                </div>
              </div>
            ) : null}
          </Card>
        )}

        {/* ============================================================ */}
        {/* INVOICE SUBMISSION (Post-Completion)                         */}
        {/* ============================================================ */}
        
        {(job?.status === 'Completed' || job?.contractor_status === 'Completed' || job?.contractor_status === 'Work Complete') && (
          <Card className="p-5 border-2 border-blue-200" data-testid="invoice-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-xl">
                  <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Submit Invoice</h3>
                  <p className="text-sm text-slate-500">Job completed - submit your invoice</p>
                </div>
              </div>
              {job?.invoice_status === 'Submitted' && (
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                  Invoice Submitted
                </Badge>
              )}
            </div>

            {job?.invoice_status === 'Submitted' ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <svg className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="font-medium text-emerald-800">Invoice Already Submitted</p>
                    {job?.invoice_amount_ex_vat && (
                      <p className="text-sm text-emerald-600">Amount: £{parseFloat(job.invoice_amount_ex_vat).toFixed(2)} ex VAT</p>
                    )}
                  </div>
                </div>
              </div>
            ) : !showInvoiceForm ? (
              <div className="space-y-4">
                {/* Show approved quote value if available */}
                {(job?.approved_value_ex_vat || job?.approved_value_inc_vat) && (
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Approved Quote Value</span>
                      <span className="font-bold text-slate-900">
                        £{parseFloat(job.approved_value_ex_vat || job.approved_value_inc_vat).toFixed(2)} ex VAT
                      </span>
                    </div>
                    {job?.po_number && (
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-slate-600">PO Number</span>
                        <span className="font-mono text-teal-600">{job.po_number}</span>
                      </div>
                    )}
                    <p className="text-xs text-slate-400 mt-3">
                      Invoice amount must be within 10% of approved value
                    </p>
                  </div>
                )}
                <Button 
                  onClick={() => setShowInvoiceForm(true)} 
                  className="w-full"
                  data-testid="show-invoice-form-btn"
                >
                  <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Create Invoice
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Error message */}
                {invoiceError && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
                    <div className="flex items-start gap-2">
                      <svg className="h-5 w-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{invoiceError}</span>
                    </div>
                  </div>
                )}

                {/* Invoice Number (optional) */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Your Invoice Number (optional)
                  </label>
                  <Input
                    type="text"
                    placeholder="e.g., INV-2024-001"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    data-testid="invoice-number-input"
                  />
                </div>

                {/* Invoice Amount */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Invoice Amount (Ex VAT) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium">£</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      className="pl-8"
                      value={invoiceAmount}
                      onChange={(e) => setInvoiceAmount(e.target.value)}
                      data-testid="invoice-amount-input"
                    />
                  </div>
                  {job?.approved_value_ex_vat && (
                    <p className="text-xs text-slate-500 mt-1">
                      Approved: £{parseFloat(job.approved_value_ex_vat).toFixed(2)} | Max: £{(parseFloat(job.approved_value_ex_vat) * 1.1).toFixed(2)}
                    </p>
                  )}
                </div>

                {/* VAT Preview */}
                {invoiceAmount && parseFloat(invoiceAmount) > 0 && (
                  <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                    <div className="flex justify-between text-slate-600">
                      <span>Subtotal (Ex VAT)</span>
                      <span className="font-medium">£{parseFloat(invoiceAmount).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-slate-600">
                      <span>VAT (20%)</span>
                      <span className="font-medium">£{(parseFloat(invoiceAmount) * 0.2).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold text-teal-600 pt-2 border-t border-slate-200">
                      <span>Total (Inc VAT)</span>
                      <span>£{(parseFloat(invoiceAmount) * 1.2).toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Invoice Notes (optional)
                  </label>
                  <Textarea
                    placeholder="Any additional notes..."
                    value={invoiceNotes}
                    onChange={(e) => setInvoiceNotes(e.target.value)}
                    data-testid="invoice-notes-input"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setShowInvoiceForm(false);
                      setInvoiceError("");
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmitInvoice}
                    loading={submittingInvoice}
                    className="flex-1 bg-teal-600 hover:bg-teal-700"
                    data-testid="submit-invoice-btn"
                  >
                    Submit Invoice
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Nearby Jobs (Same Postcode Area) */}
        {nearbyJobs.length > 0 && (
          <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="text-lg font-semibold text-slate-900">Nearby Jobs</div>
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                {nearbyJobs.length} job{nearbyJobs.length !== 1 ? 's' : ''}
              </span>
            </div>
            <p className="text-sm text-slate-500 mb-4">Other jobs in the same area you can combine visits for:</p>
            <div className="space-y-2">
              {nearbyJobs.map((nj) => (
                <div 
                  key={nj.id} 
                  className="flex items-center justify-between p-3 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer"
                  onClick={() => window.location.href = `/contractor/job/${nj.id}?token=${encodeURIComponent(token)}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-800 truncate">{nj.site_name || nj.park_name}</p>
                    <p className="text-xs text-slate-500 truncate">{nj.description?.substring(0, 60)}...</p>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      nj.priority?.toLowerCase() === 'high' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {nj.priority || 'Normal'}
                    </span>
                    <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Same Site Jobs */}
        {sameSiteJobs.length > 0 && (
          <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="text-lg font-semibold text-slate-900">Other Jobs on This Site</div>
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-teal-100 text-teal-700">
                {sameSiteJobs.length} job{sameSiteJobs.length !== 1 ? 's' : ''}
              </span>
            </div>
            <p className="text-sm text-slate-500 mb-4">Other jobs at {job?.site_name || job?.park_name}:</p>
            <div className="space-y-2">
              {sameSiteJobs.map((sj) => (
                <div 
                  key={sj.id} 
                  className="flex items-center justify-between p-3 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer"
                  onClick={() => window.location.href = `/contractor/job/${sj.id}?token=${encodeURIComponent(token)}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-800 truncate">{sj.asset_name || sj.unit_name || 'Unit'}</p>
                    <p className="text-xs text-slate-500 truncate">{sj.job_type || sj.description?.substring(0, 40)}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      sj.status === 'Scheduled' ? 'bg-blue-100 text-blue-700' :
                      sj.status === 'Completed' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {sj.status}
                    </span>
                    <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        <div className="text-center text-xs text-slate-400 py-4">
          By submitting documents or quotes, you confirm all information is accurate and work will be completed to industry standards.
        </div>
      </main>
    </div>
  );
}
